/**
 * Component: IncentiveRuleStep
 * Step 2 form fields for Program creation/edit - Incentive Rule configuration.
 */

import { useState } from "react";
import { Select } from "@/components/ui/select";
import { FileUpload } from "@/components/ui/file-upload";
import { SearchInput } from "@/components/ui/search-input";
import { Radio } from "@/components/ui/radio";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Pagination,
} from "@/components/ui/table/table";
import { Input } from "@/components/ui/input";
import { MaskedNumberInput } from "@/components/ui/masked-input";
import { Button } from "@/components/ui/button";
import type {
  ProgramFormData,
  IncentiveRuleType,
  ProductIncentiveData,
  BaseRewardLevel,
} from "@/feature/programs/domain/model";
import { downloadProductIncentiveCsvTemplate } from "../utils";
import { TrashIcon } from "@/assets/svg";
import { numberToString, stringToNumber } from "@/utils/Format";

const basedOnOptions = [
  { value: "", label: "Select Based On", disabled: true },
  { value: "transaction", label: "Transaction" },
  { value: "revenue", label: "Revenue" },
];

interface IncentiveRuleStepProps {
  formData: ProgramFormData;
  errors: Partial<Record<keyof ProgramFormData, string>>;
  onInputChange: (field: keyof ProgramFormData, value: string | number) => void;
  onProductIncentiveFileChange: (fileName: string, file?: File | null) => void;
  productIncentiveFileError?: string | null;
  isUploading?: boolean;
  onBaseRewardLevelsChange?: (levels: BaseRewardLevel[]) => void;
}

export const IncentiveRuleStep = ({
  formData,
  errors,
  onInputChange,
  onProductIncentiveFileChange,
  productIncentiveFileError,
  isUploading = false,
  onBaseRewardLevelsChange,
}: IncentiveRuleStepProps): React.JSX.Element => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const isDuwitChannel = formData.channel === "Duwit";

  // Filter product incentive list by search query
  const filteredProducts = formData.productIncentiveList.filter(
    (product: ProductIncentiveData) =>
      product.productId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.productName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / itemsPerPage),
  );
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Duwit-specific handlers
  const handleAddLevel = () => {
    const newLevel: BaseRewardLevel = {
      id: crypto.randomUUID(),
      minTransaction: 0,
      maxTransaction: 0,
      pointsEarned: 0,
    };
    onBaseRewardLevelsChange?.([...formData.baseRewardLevels, newLevel]);
  };

  const handleRemoveLevel = (levelId: string) => {
    onBaseRewardLevelsChange?.(
      formData.baseRewardLevels.filter((level) => level.id !== levelId),
    );
  };

  const handleLevelChange = (
    levelId: string,
    field: keyof Omit<BaseRewardLevel, "id">,
    value: string,
  ) => {
    const numericValue = value === "" ? 0 : Number(value);
    onBaseRewardLevelsChange?.(
      formData.baseRewardLevels.map((level) =>
        level.id === levelId ? { ...level, [field]: numericValue } : level,
      ),
    );
  };

  // Render Duwit-specific form
  if (isDuwitChannel) {
    return (
      <div className="flex flex-col gap-6">
        {/* Base Reward Section */}
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">Base Reward</h3>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAddLevel}
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 3.33334V12.6667"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.33334 8H12.6667"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
          >
            Add Level
          </Button>
        </div>

        {/* Levels */}
        {formData.baseRewardLevels.map((level, index) => (
          <div key={level.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-gray-800">
                Level {index + 1}
              </h4>
              <button
                type="button"
                onClick={() => handleRemoveLevel(level.id)}
                className="p-2.5 text-red-500 bg-transparent hover:bg-red-50 rounded-lg transition-colors border-red-600 hover:border-red-700"
                aria-label={`Remove Level ${index + 1}`}
              >
                <TrashIcon />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Total Transaction Range */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-800 text-left">
                  Total Transaction Range (Rp)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <MaskedNumberInput
                    value={numberToString(level.minTransaction)}
                    onChange={(value) =>
                      handleLevelChange(level.id, "minTransaction", value)
                    }
                    placeholder="Min"
                  />
                  <MaskedNumberInput
                    value={numberToString(level.maxTransaction)}
                    onChange={(value) =>
                      handleLevelChange(level.id, "maxTransaction", value)
                    }
                    placeholder="Max"
                  />
                </div>
              </div>

              {/* Points Earned */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-800 text-left">
                  Points Earned
                </label>
                <Input
                  type="number"
                  value={numberToString(level.pointsEarned)}
                  onChange={(e) =>
                    handleLevelChange(level.id, "pointsEarned", e.target.value)
                  }
                  placeholder="Points"
                />
              </div>
            </div>
          </div>
        ))}

        {formData.baseRewardLevels.length === 0 && (
          <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
            No levels added. Click "Add Level" to create a reward tier.
          </div>
        )}

        {/* Maximum Base Reward Points Per Day */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-gray-800 text-left">
            Maximum Base Reward Points Per Day
          </label>
          <div className="w-1/4">
            <Input
              type="number"
              value={numberToString(formData.maxBaseRewardPointsPerDay)}
              onChange={(e) =>
                onInputChange("maxBaseRewardPointsPerDay", stringToNumber(e.target.value))
              }
              placeholder="Max Points"
              errorMessage={errors.maxBaseRewardPointsPerDay}
            />
          </div>
        </div>

        {/* Stamp & Multiplier Section */}
        <h3 className="text-base font-bold text-gray-900 mt-4 text-left">
          Stamp & Multiplier
        </h3>

        {/* Minimum Total Transaction */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-gray-800 text-left">
            Minimum Total Transaction (Rp)
          </label>
          <div className="w-1/4">
            <MaskedNumberInput
              value={numberToString(formData.stampMinimumTransaction)}
              onChange={(value) =>
                onInputChange("stampMinimumTransaction", stringToNumber(value))
              }
              placeholder="100000"
              errorMessage={errors.stampMinimumTransaction}
            />
          </div>
        </div>

        {/* Weekly Stamp */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-bold text-gray-800 mb-4 text-left">
            Weekly Stamp
          </h4>
          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-800 text-left">
                Minimum Active Days
              </label>
              <Input
                type="number"
                value={numberToString(formData.weeklyStampMinDays)}
                onChange={(e) =>
                  onInputChange("weeklyStampMinDays", stringToNumber(e.target.value))
                }
                placeholder="Days"
                errorMessage={errors.weeklyStampMinDays}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-800 text-left">
                Multiplier Percentage (%)
              </label>
              <Input
                type="number"
                value={numberToString(formData.weeklyStampMultiplier)}
                onChange={(e) =>
                  onInputChange("weeklyStampMultiplier", stringToNumber(e.target.value))
                }
                placeholder="Percentage"
                errorMessage={errors.weeklyStampMultiplier}
              />
            </div>
          </div>
        </div>

        {/* Monthly Stamp */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-bold text-gray-800 mb-4 text-left">
            Monthly Stamp
          </h4>
          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-800 text-left">
                Minimum Active Days
              </label>
              <Input
                type="number"
                value={numberToString(formData.monthlyStampMinDays)}
                onChange={(e) =>
                  onInputChange("monthlyStampMinDays", stringToNumber(e.target.value))
                }
                placeholder="Days"
                errorMessage={errors.monthlyStampMinDays}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-800 text-left">
                Multiplier Percentage (%)
              </label>
              <Input
                type="number"
                value={numberToString(formData.monthlyStampMultiplier)}
                onChange={(e) =>
                  onInputChange("monthlyStampMultiplier", stringToNumber(e.target.value))
                }
                placeholder="Percentage"
                errorMessage={errors.monthlyStampMultiplier}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render non-Duwit form (original layout)
  return (
    <div className="flex flex-col gap-6">
      {/* Row 1: Incentive Rule Radio Buttons and Based On Dropdown */}
      <div className="grid grid-cols-2 gap-6">
        {/* Incentive Rule */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-gray-800 text-left">
            Incentive Rule
          </label>
          <div className="flex gap-4">
            <Radio
              name="incentiveRule"
              value="general"
              label="General Incentive"
              checked={formData.incentiveRule === "general"}
              onChange={(e) =>
                onInputChange(
                  "incentiveRule",
                  e.target.value as IncentiveRuleType,
                )
              }
              variant="card"
              className="flex-1"
            />
            <Radio
              name="incentiveRule"
              value="product"
              label="Product Incentive"
              checked={formData.incentiveRule === "product"}
              onChange={(e) =>
                onInputChange(
                  "incentiveRule",
                  e.target.value as IncentiveRuleType,
                )
              }
              variant="card"
              className="flex-1"
            />
          </div>
        </div>

        {/* Based On */}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="based-on"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Based On
          </label>
          <Select
            id="based-on"
            value={formData.basedOn}
            onChange={(e) => onInputChange("basedOn", e.target.value)}
            options={basedOnOptions}
            errorMessage={errors.basedOn}
          />
        </div>
      </div>

      {/* Row 2: Minimum Transaction and Maximum Transaction */}
      <div className="grid grid-cols-2 gap-6">
        {/* Minimum Transaction */}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="min-transaction"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Minimum Transaction
          </label>
          <Input
            id="min-transaction"
            type="number"
            min="1"
            value={formData.minTransaction}
            onChange={(e) => onInputChange("minTransaction", e.target.value)}
            placeholder="Minimum Transaction"
            errorMessage={errors.minTransaction}
          />
        </div>

        {/* Maximum Transaction */}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="max-transaction"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Maximum Transaction
          </label>
          <Input
            id="max-transaction"
            type="number"
            min="1"
            value={formData.maxTransaction}
            onChange={(e) => onInputChange("maxTransaction", e.target.value)}
            placeholder="Maximum Transaction"
            errorMessage={errors.maxTransaction}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6">
        {/* Product Incentive File - only shown when Product Incentive is selected */}
        {formData.incentiveRule === "product" && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-800 text-left">
              Product Incentive File
            </label>
            {isUploading ? (
              <div className="flex items-center justify-center h-24 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                <div className="flex items-center gap-2 text-gray-500">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Uploading file...</span>
                </div>
              </div>
            ) : (
              <FileUpload
                id="product-incentive-file"
                value={formData.productIncentiveFile}
                onChange={(fileName, file) =>
                  onProductIncentiveFileChange(fileName, file)
                }
                accept=".csv"
                acceptText="File type: .csv | Maximum size: 5MB"
                templateLink={{
                  label: "template.csv",
                  onClick: downloadProductIncentiveCsvTemplate,
                }}
                variant="document"
                showIcon={false}
                errorMessage={productIncentiveFileError ?? undefined}
              />
            )}
            {formData.productTotalCount > 0 && (
              <span className="text-sm text-gray-600">
                Total products: {formData.productTotalCount.toLocaleString()}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Product Incentive List - only shown when Product Incentive is selected */}
      {formData.incentiveRule === "product" && (
        <div className="flex flex-col gap-4">
          <h3 className="text-base font-bold text-gray-900 text-left">
            Product Incentive List
          </h3>

          {/* Search */}
          <div className="w-64">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search"
            />
          </div>

          {/* Table */}
          <Table>
            <TableHeader>
              <tr>
                <TableHead>No.</TableHead>
                <TableHead>Product ID</TableHead>
                <TableHead>Product Name</TableHead>
                <TableHead>Product Description</TableHead>
                <TableHead>Point Type</TableHead>
                <TableHead>Point Incentive</TableHead>
              </tr>
            </TableHeader>
            <TableBody>
              {paginatedProducts.length > 0 ? (
                paginatedProducts.map(
                  (product: ProductIncentiveData, index: number) => (
                    <TableRow key={product.productId}>
                      <TableCell>
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </TableCell>
                      <TableCell>{product.productId}</TableCell>
                      <TableCell>{product.productName}</TableCell>
                      <TableCell>{product.productDescription}</TableCell>
                      <TableCell>{product.pointType}</TableCell>
                      <TableCell>{product.pointIncentive}</TableCell>
                    </TableRow>
                  ),
                )
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-500">
                    No Data
                  </td>
                </tr>
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};
